import type { Point, Reference, Shape } from '@/features/sheets/contract/schema';

export const shapePoints = (shape: Shape) =>
  shape.points.map((point) => `${round(point.x)},${round(point.y)}`).join(' ');

const round = (value: number) => Math.round(value * 100) / 100;

export const shapeAnchor = (shape: Shape): Point => {
  const middle = shape.points[Math.floor((shape.points.length - 1) / 2)];
  if (shape.kind === 'polyline' && middle) {
    const next = shape.points[Math.floor((shape.points.length - 1) / 2) + 1] ?? middle;
    return { x: (middle.x + next.x) / 2, y: (middle.y + next.y) / 2 };
  }
  const sum = shape.points.reduce(
    (total, point) => ({ x: total.x + point.x, y: total.y + point.y }),
    {
      x: 0,
      y: 0,
    },
  );
  return { x: sum.x / shape.points.length, y: sum.y / shape.points.length };
};

export const measuredTotals = (shapes: Shape[]) => {
  const totals: { unit: string | null; value: number }[] = [];
  for (const shape of shapes) {
    if (shape.value === null) continue;
    const total = totals.find((item) => item.unit === shape.unit);
    if (total) total.value += shape.value;
    else totals.push({ unit: shape.unit, value: shape.value });
  }
  return totals;
};

export const shapeLegend = (shapes: Shape[]) =>
  [...Map.groupBy(shapes, (shape) => shape.label)].map(([label, group], slot) => ({
    label,
    slot,
    count: group.length,
    totals: measuredTotals(group),
  }));

export const shapeSlots = (shapes: Shape[]) => {
  const slots = new Map(shapeLegend(shapes).map((entry) => [entry.label, entry.slot]));
  return shapes.map((shape) => slots.get(shape.label) ?? 0);
};

export const pageShapes = (references: Reference[], page: number | null) =>
  references
    .filter((reference) => reference.page === page)
    .flatMap((reference) => reference.shapes ?? []);

export const formatMeasure = (value: number, unit: string | null, locale: string) =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)}${unit ? ` ${unit}` : ''}`;

export const displayedPageSize = (viewport: { width: number; height: number; scale: number }) => ({
  width: viewport.width / viewport.scale,
  height: viewport.height / viewport.scale,
});
